import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import {
  Star, Image as ImageIcon, FileText, Plus, Clock, BarChart3, ShieldCheck, Sparkles, Heart, Globe2, Users,
} from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis } from "recharts";

type Review = { id: string; name: string; rating: number; treatment: string | null; is_published: boolean; created_at: string };
type GalleryItem = { id: string; treatment: string; is_published: boolean; created_at: string };
type Blog = { id: string; title: string; is_published: boolean; created_at: string };

const AdminDashboard = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const [r, g, b] = await Promise.all([
        supabase.from("reviews").select("id, name, rating, treatment, is_published, created_at").order("created_at", { ascending: false }),
        supabase.from("gallery_items").select("id, treatment, is_published, created_at").order("created_at", { ascending: false }),
        supabase.from("blogs").select("id, title, is_published, created_at").order("created_at", { ascending: false }),
      ]);
      setReviews(r.data || []);
      setGallery(g.data || []);
      setBlogs(b.data || []);
      setLoading(false);
    };
    fetch();
  }, []);

  const pubReviews = reviews.filter(r => r.is_published).length;
  const pubGallery = gallery.filter(g => g.is_published).length;
  const pubBlogs = blogs.filter(b => b.is_published).length;

  // Reviews by treatment
  const treatmentMap: Record<string, number> = {};
  reviews.forEach(r => {
    const t = r.treatment || "Other";
    treatmentMap[t] = (treatmentMap[t] || 0) + 1;
  });
  const treatmentData = Object.entries(treatmentMap).map(([name, count]) => ({ name: name.length > 15 ? name.slice(0, 15) + "…" : name, count })).sort((a, b) => b.count - a.count);

  // Rating distribution
  const ratingMap: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach(r => { ratingMap[r.rating] = (ratingMap[r.rating] || 0) + 1; });
  const ratingData = [5, 4, 3, 2, 1].map(r => ({ rating: `${r}★`, count: ratingMap[r] }));

  // Recent activity
  const activity = [
    ...reviews.slice(0, 5).map(r => ({ type: "Review", label: r.name, date: r.created_at })),
    ...gallery.slice(0, 5).map(g => ({ type: "Gallery", label: g.treatment, date: g.created_at })),
    ...blogs.slice(0, 5).map(b => ({ type: "Blog", label: b.title, date: b.created_at })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8);

  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "–";

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* HERO — Command Center */}
      <section className="admin-gradient-hero rounded-3xl px-6 lg:px-10 py-8 lg:py-10">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <p className="text-[11px] tracking-[0.3em] font-semibold uppercase text-[hsl(178,80%,75%)] flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
              Command Center Active
            </p>
            <h1 className="font-heading text-3xl lg:text-5xl font-semibold mt-3 leading-tight">
              Intelligence <span className="italic font-light text-[hsl(178,80%,75%)]">Report</span>
            </h1>
            <p className="text-white/70 mt-3 max-w-lg text-sm lg:text-base">
              Welcome back. Your clinic content system is operational.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="px-4 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15 min-w-[180px]">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Session State
              </p>
              <p className="text-sm font-semibold mt-1.5 text-white">Secure Connection</p>
            </div>
            <div className="px-4 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15 min-w-[180px]">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Local Time
              </p>
              <p className="text-sm font-semibold mt-1.5 text-white">
                {now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STAT CARDS */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        <StatCard icon={<FileText className="h-5 w-5" />} value={pubBlogs} label="Published Articles" tint="teal" />
        <StatCard icon={<Heart className="h-5 w-5" />} value={reviews.length} label="Total Reviews" tint="rose"
          chip={reviews.length - pubReviews > 0 ? `+${reviews.length - pubReviews} draft` : undefined} />
        <StatCard icon={<Globe2 className="h-5 w-5" />} value={pubGallery} label="Live Gallery Items" tint="navy" />
        <StatCard icon={<Users className="h-5 w-5" />} value={avgRating} label="Average Rating" tint="gold"
          chip={`${reviews.length} reviews`} />
      </section>

      {/* LEAD INTELLIGENCE banner */}
      <section className="admin-gradient-hero rounded-3xl px-6 lg:px-10 py-7 lg:py-8">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl lg:text-3xl font-semibold">Content Intelligence</h2>
            <p className="text-white/70 text-sm lg:text-base mt-1">Real-time monitoring across reviews, gallery and blog.</p>
          </div>
          <div className="flex gap-3">
            <div className="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60">Total Entries</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-white">{reviews.length + gallery.length + blogs.length}</p>
            </div>
            <div className="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60">Published</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-white">{pubReviews + pubGallery + pubBlogs}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CHARTS + ACTIVITY + QUICK ACTIONS */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Reviews by Treatment */}
        <div className="admin-card p-6 lg:col-span-2">
          <p className="admin-eyebrow flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" /> Reviews by Treatment</p>
          <h3 className="font-heading text-xl font-semibold mt-1 mb-4">Patient Voice Distribution</h3>
          {treatmentData.length > 0 ? (
            <ChartContainer config={{ count: { label: "Reviews", color: "hsl(var(--admin-teal))" } }} className="h-[240px] w-full">
              <BarChart data={treatmentData} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "hsl(var(--admin-muted))" }} axisLine={false} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill="hsl(var(--admin-teal))" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ChartContainer>
          ) : (
            <p className="text-sm text-[hsl(var(--admin-muted))] text-center py-10">No review data yet</p>
          )}
        </div>

        {/* Rating distribution */}
        <div className="admin-card p-6">
          <p className="admin-eyebrow flex items-center gap-2"><Star className="h-3.5 w-3.5" /> Rating Spread</p>
          <h3 className="font-heading text-xl font-semibold mt-1 mb-4">Patient Satisfaction</h3>
          <div className="text-center mb-5">
            <p className="text-5xl font-heading font-semibold admin-gradient-text">{avgRating}</p>
            <div className="flex gap-0.5 justify-center mt-2">
              {[1,2,3,4,5].map(i => <Star key={i} className="h-3.5 w-3.5 fill-[hsl(var(--admin-gold))] text-[hsl(var(--admin-gold))]" />)}
            </div>
            <p className="text-xs text-[hsl(var(--admin-muted))] mt-1">{reviews.length} reviews</p>
          </div>
          <div className="space-y-2.5">
            {ratingData.map((d) => (
              <div key={d.rating} className="flex items-center gap-2 text-xs">
                <span className="w-6 text-right font-medium">{d.rating}</span>
                <Progress value={reviews.length ? (d.count / reviews.length) * 100 : 0} className="flex-1 h-1.5" />
                <span className="w-6 text-[hsl(var(--admin-muted))]">{d.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="admin-card p-6 lg:col-span-2">
          <p className="admin-eyebrow flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Recent Activity</p>
          <h3 className="font-heading text-xl font-semibold mt-1 mb-4">Latest Movement</h3>
          {activity.length > 0 ? (
            <div className="space-y-3">
              {activity.map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--admin-bg))] text-sm">
                  <span className={`text-[10px] tracking-wider uppercase px-2 py-1 rounded font-semibold ${
                    a.type === "Review" ? "bg-[hsl(var(--admin-teal))]/12 text-[hsl(var(--admin-teal-deep))]" :
                    a.type === "Gallery" ? "bg-[hsl(var(--admin-navy))]/10 text-[hsl(var(--admin-navy))]" :
                    "bg-[hsl(var(--admin-gold))]/15 text-[hsl(36,70%,40%)]"
                  }`}>{a.type}</span>
                  <span className="flex-1 truncate font-medium">{a.label}</span>
                  <span className="text-xs text-[hsl(var(--admin-muted))] shrink-0">
                    {new Date(a.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--admin-muted))] text-center py-6">No activity yet</p>
          )}
        </div>

        {/* System ops */}
        <div className="admin-card p-6">
          <p className="admin-eyebrow flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> System Operations</p>
          <h3 className="font-heading text-xl font-semibold mt-1 mb-4">Quick Actions</h3>
          <div className="space-y-2.5">
            <QuickAction to="/admin/reviews" icon={<Star className="h-4 w-4" />} label="Add Review" />
            <QuickAction to="/admin/gallery" icon={<ImageIcon className="h-4 w-4" />} label="Add Gallery Item" />
            <QuickAction to="/admin/blog" icon={<FileText className="h-4 w-4" />} label="Write Blog Post" />
          </div>
        </div>
      </div>
    </div>
  );
};

const tintMap: Record<string, { bg: string; fg: string }> = {
  teal:  { bg: "bg-[hsl(var(--admin-teal))]/10", fg: "text-[hsl(var(--admin-teal-deep))]" },
  navy:  { bg: "bg-[hsl(var(--admin-navy))]/10", fg: "text-[hsl(var(--admin-navy))]" },
  rose:  { bg: "bg-rose-100", fg: "text-rose-600" },
  gold:  { bg: "bg-[hsl(var(--admin-gold))]/15", fg: "text-[hsl(36,75%,40%)]" },
};

const StatCard = ({ icon, value, label, chip, tint }: { icon: React.ReactNode; value: string | number; label: string; chip?: string; tint: keyof typeof tintMap }) => {
  const t = tintMap[tint];
  return (
    <div className="admin-card p-5 lg:p-6 relative overflow-hidden">
      <div className={`h-11 w-11 rounded-2xl flex items-center justify-center mb-5 ${t.bg} ${t.fg}`}>{icon}</div>
      <p className="font-heading text-4xl lg:text-5xl font-semibold leading-none tracking-tight">{value}</p>
      <p className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--admin-muted))] mt-3 font-semibold">{label}</p>
      {chip && (
        <span className="inline-flex items-center mt-2 text-[11px] font-semibold text-emerald-600">
          <Sparkles className="h-3 w-3 mr-1" /> {chip}
        </span>
      )}
    </div>
  );
};

const QuickAction = ({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[hsl(var(--admin-border))] hover:border-[hsl(var(--admin-teal))] hover:bg-[hsl(var(--admin-teal))]/5 transition text-sm font-medium"
  >
    <span className="h-8 w-8 rounded-lg bg-[hsl(var(--admin-teal))]/10 text-[hsl(var(--admin-teal-deep))] flex items-center justify-center">{icon}</span>
    <span className="flex-1">{label}</span>
    <Plus className="h-4 w-4 text-[hsl(var(--admin-muted))]" />
  </Link>
);

export default AdminDashboard;
